import { useState, FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { Loader2, Mail, ArrowLeft, CheckCircle } from 'lucide-react';
import LogoIcon from '../components/LogoIcon';
import { forgotPassword } from '../services/api';
import toast from 'react-hot-toast';

export default function ForgotPasswordPage() {
    const [email,   setEmail]   = useState('');
    const [loading, setLoading] = useState(false);
    const [sent,    setSent]    = useState(false);
    const [devLink, setDevLink] = useState<string | null>(null);

    const handleSubmit = async (e: FormEvent) => {
        e.preventDefault();
        if (!email) return;
        setLoading(true);
        try {
            const res = await forgotPassword(email);
            setSent(true);
            // Dev environments return the reset link directly in the response
            if (res.dev_reset_link) setDevLink(res.dev_reset_link);
        } catch (err: unknown) {
            const msg = (err as { response?: { data?: { error?: string } } })
                ?.response?.data?.error || '请求失败，请稍后重试';
            toast.error(msg);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center bg-surface-0 px-4">
            <div className="w-full max-w-sm">
                {/* Logo */}
                <div className="flex flex-col items-center mb-8">
                    <div className="mb-4 shadow-lg shadow-brand-900/50 rounded-2xl">
                        <LogoIcon size={48} />
                    </div>
                    <h1 className="text-2xl font-bold text-white">税码通</h1>
                    <p className="text-slate-500 text-sm mt-1">AI 智能 HTS 编码分类平台</p>
                </div>

                <div className="card">
                    {sent ? (
                        <div className="text-center py-4">
                            <CheckCircle size={40} className="text-emerald-400 mx-auto mb-4" />
                            <h2 className="text-lg font-semibold text-white mb-2">邮件已发送</h2>
                            <p className="text-slate-400 text-sm leading-relaxed">
                                如果 <span className="text-white font-medium">{email}</span> 已在系统注册，
                                重置密码链接将在几分钟内送达。请检查收件箱（含垃圾箱）。
                            </p>

                            {devLink && (
                                <div className="mt-4 p-3 bg-amber-500/10 border border-amber-500/20 rounded-xl text-left">
                                    <p className="text-xs text-amber-400 font-semibold mb-1">开发模式 — 重置链接</p>
                                    <a
                                        href={devLink}
                                        className="text-xs text-brand-400 hover:text-brand-300 break-all transition-colors"
                                    >
                                        {devLink}
                                    </a>
                                </div>
                            )}

                            <button
                                onClick={() => { setSent(false); setDevLink(null); }}
                                className="btn-secondary mt-6 w-full text-sm"
                            >
                                重新发送
                            </button>
                        </div>
                    ) : (
                        <>
                            <div className="flex items-center gap-2 mb-1">
                                <Mail size={16} className="text-brand-400" />
                                <h2 className="text-lg font-semibold text-white">找回密码</h2>
                            </div>
                            <p className="text-slate-500 text-sm mb-6">
                                输入注册邮箱，我们将发送密码重置链接
                            </p>

                            <form onSubmit={handleSubmit} className="space-y-4">
                                <div>
                                    <label className="block text-xs font-medium text-slate-400 mb-1.5">
                                        注册邮箱
                                    </label>
                                    <input
                                        type="email"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        placeholder="you@example.com"
                                        required
                                        autoFocus
                                        className="input-field"
                                    />
                                </div>

                                <button
                                    type="submit"
                                    disabled={loading || !email}
                                    className="btn-primary w-full mt-2"
                                >
                                    {loading ? (
                                        <><Loader2 size={16} className="animate-spin" /> 发送中...</>
                                    ) : (
                                        '发送重置邮件'
                                    )}
                                </button>
                            </form>
                        </>
                    )}

                    <p className="text-center text-sm text-slate-500 mt-5">
                        <Link
                            to="/login"
                            className="flex items-center justify-center gap-1.5 text-slate-400 hover:text-white transition-colors"
                        >
                            <ArrowLeft size={13} />
                            返回登录
                        </Link>
                    </p>
                </div>
            </div>
        </div>
    );
}
